import { httpClient } from '@/api/httpClient';
import {
  mapApiResourceToBodyArea,
  mapApiResourceToCategory,
  mapApiResourceToDevice,
  mapApiResourceToTreatment,
  mapDeviceWritePayload,
  mapTreatmentWritePayload,
  type BodyArea,
  type BodyAreaApiResource,
  type BodyAreaDraft,
  type CategoryDraft,
  type Device,
  type DeviceApiResource,
  type DeviceWritePayload,
  type Treatment,
  type TreatmentApiResource,
  type TreatmentCategory,
  type TreatmentCategoryApiResource,
  type TreatmentWritePayload,
} from './treatmentLibraryApi.types';

export async function fetchTreatmentCategories(): Promise<TreatmentCategory[]> {
  const response = await httpClient.get<TreatmentCategoryApiResource[]>('/treatment-categories');
  return response.data.map(mapApiResourceToCategory);
}

export async function replaceTreatmentCategories(
  drafts: CategoryDraft[]
): Promise<TreatmentCategory[]> {
  const items = drafts.map(function toItem(draft) {
    return {
      id: draft.id === null ? undefined : draft.id,
      name_en: draft.nameEn,
      name_pl: draft.namePl,
      color: draft.color,
    };
  });
  const response = await httpClient.put<TreatmentCategoryApiResource[]>('/treatment-categories', {
    items,
  });
  return response.data.map(mapApiResourceToCategory);
}

export async function fetchBodyAreas(): Promise<BodyArea[]> {
  const response = await httpClient.get<BodyAreaApiResource[]>('/body-areas');
  return response.data.map(mapApiResourceToBodyArea);
}

export async function replaceBodyAreas(drafts: BodyAreaDraft[]): Promise<BodyArea[]> {
  const items = drafts.map(function toItem(draft) {
    return {
      id: draft.id === null ? undefined : draft.id,
      name_en: draft.nameEn,
      name_pl: draft.namePl,
    };
  });
  const response = await httpClient.put<BodyAreaApiResource[]>('/body-areas', { items });
  return response.data.map(mapApiResourceToBodyArea);
}

export async function fetchDevices(): Promise<Device[]> {
  const response = await httpClient.get<DeviceApiResource[]>('/devices');
  return response.data.map(mapApiResourceToDevice);
}

export async function createDevice(payload: DeviceWritePayload): Promise<Device> {
  const response = await httpClient.post<DeviceApiResource>(
    '/devices',
    mapDeviceWritePayload(payload)
  );
  return mapApiResourceToDevice(response.data);
}

export async function updateDevice(deviceId: string, payload: DeviceWritePayload): Promise<Device> {
  const response = await httpClient.patch<DeviceApiResource>(
    '/devices/' + deviceId,
    mapDeviceWritePayload(payload)
  );
  return mapApiResourceToDevice(response.data);
}

export async function setDeviceActive(deviceId: string, isActive: boolean): Promise<Device> {
  const action = isActive ? 'activate' : 'deactivate';
  const response = await httpClient.post<DeviceApiResource>('/devices/' + deviceId + '/' + action);
  return mapApiResourceToDevice(response.data);
}

export async function fetchTreatments(
  search: string,
  categoryId: string | null,
  includeInactive: boolean
): Promise<Treatment[]> {
  const trimmed = search.trim();
  const response = await httpClient.get<TreatmentApiResource[]>('/treatments', {
    params: {
      search: trimmed.length === 0 ? undefined : trimmed,
      category_id: categoryId === null ? undefined : categoryId,
      include_inactive: includeInactive ? true : undefined,
    },
  });
  return response.data.map(mapApiResourceToTreatment);
}

export async function createTreatment(payload: TreatmentWritePayload): Promise<Treatment> {
  const response = await httpClient.post<TreatmentApiResource>(
    '/treatments',
    mapTreatmentWritePayload(payload)
  );
  return mapApiResourceToTreatment(response.data);
}

export async function updateTreatment(
  treatmentId: string,
  payload: TreatmentWritePayload
): Promise<Treatment> {
  const response = await httpClient.patch<TreatmentApiResource>(
    '/treatments/' + treatmentId,
    mapTreatmentWritePayload(payload)
  );
  return mapApiResourceToTreatment(response.data);
}

export async function setTreatmentActive(
  treatmentId: string,
  isActive: boolean
): Promise<Treatment> {
  const action = isActive ? 'activate' : 'deactivate';
  const response = await httpClient.post<TreatmentApiResource>(
    '/treatments/' + treatmentId + '/' + action
  );
  return mapApiResourceToTreatment(response.data);
}

function buildImageFormData(file: File): FormData {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
}

const MULTIPART_HEADERS = { headers: { 'Content-Type': 'multipart/form-data' } };

export async function uploadDeviceImage(deviceId: string, file: File): Promise<Device> {
  const response = await httpClient.post<DeviceApiResource>(
    '/devices/' + deviceId + '/image',
    buildImageFormData(file),
    MULTIPART_HEADERS
  );
  return mapApiResourceToDevice(response.data);
}

export async function removeDeviceImage(deviceId: string): Promise<Device> {
  const response = await httpClient.delete<DeviceApiResource>('/devices/' + deviceId + '/image');
  return mapApiResourceToDevice(response.data);
}

export async function uploadTreatmentImage(treatmentId: string, file: File): Promise<Treatment> {
  const response = await httpClient.post<TreatmentApiResource>(
    '/treatments/' + treatmentId + '/image',
    buildImageFormData(file),
    MULTIPART_HEADERS
  );
  return mapApiResourceToTreatment(response.data);
}

export async function removeTreatmentImage(treatmentId: string): Promise<Treatment> {
  const response = await httpClient.delete<TreatmentApiResource>(
    '/treatments/' + treatmentId + '/image'
  );
  return mapApiResourceToTreatment(response.data);
}

export async function uploadCategoryImage(
  categoryId: string,
  file: File
): Promise<TreatmentCategory> {
  const response = await httpClient.post<TreatmentCategoryApiResource>(
    '/treatment-categories/' + categoryId + '/image',
    buildImageFormData(file),
    MULTIPART_HEADERS
  );
  return mapApiResourceToCategory(response.data);
}

export async function removeCategoryImage(categoryId: string): Promise<TreatmentCategory> {
  const response = await httpClient.delete<TreatmentCategoryApiResource>(
    '/treatment-categories/' + categoryId + '/image'
  );
  return mapApiResourceToCategory(response.data);
}

export async function uploadBodyAreaImage(bodyAreaId: string, file: File): Promise<BodyArea> {
  const response = await httpClient.post<BodyAreaApiResource>(
    '/body-areas/' + bodyAreaId + '/image',
    buildImageFormData(file),
    MULTIPART_HEADERS
  );
  return mapApiResourceToBodyArea(response.data);
}

export async function removeBodyAreaImage(bodyAreaId: string): Promise<BodyArea> {
  const response = await httpClient.delete<BodyAreaApiResource>(
    '/body-areas/' + bodyAreaId + '/image'
  );
  return mapApiResourceToBodyArea(response.data);
}
